import type { Store as StoreService } from '@warp-drive/core';
import { executeSearchRequest } from 'tio-data/requests/search';
import type { OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import { OpenSearchQueryBuilder } from './open-search-builder';
import { OpenSearchResponseBuilder } from './open-search-response-builder';

export interface EmployeeLookupResult {
  employeeId: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email?: string;
}

// Internal type for OpenSearch Person response after normalization
// OpenSearch returns employee_ids as array of numbers, which gets normalized to employeeIds
interface PersonResponse {
  id: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  email: string;
  employeeIds?: number[]; // Normalized from employee_ids - these are the actual employee IDs (not payroll IDs)
}

/**
 * Fetch employee/person records by employee IDs using OpenSearch
 *
 * @param store - Ember Data store service
 * @param employeeIds - Array of employee IDs (numbers or strings) to lookup
 * @param companyId - Optional company ID to filter by (required for non-admin tokens via SearchPolicy)
 * @returns Map of employee ID (as string) to person data
 *
 * @example
 * ```typescript
 * const employees = await lookupEmployeesByIds(this.store, ['123', '456', '789']);
 * const name = employees.get('123')?.fullName ?? 'Unknown';
 * ```
 */
export async function lookupEmployeesByIds(
  store: StoreService,
  employeeIds: Array<string | number>,
  companyId?: string
): Promise<Map<string, EmployeeLookupResult>> {
  if (!employeeIds.length) {
    return new Map();
  }

  // Convert all IDs to strings for consistent handling
  const stringIds = employeeIds.map((id) => String(id));

  // Build OpenSearch query - CRITICAL: Use snake_case for OpenSearch fields
  // employee_ids is mapped server-side to all_employee_ids_keyword via INBOUND_FIELD_MAPS
  const builder = new OpenSearchQueryBuilder()
    .setPagination(1, stringIds.length) // Get all results in one page
    .mustTerms('employee_ids', stringIds); // Filter by employee IDs

  // Person search may require company_id filter depending on token role (validated by SearchPolicy)
  if (companyId) {
    builder.mustTerm('company_id', companyId);
  }

  const query = {
    'model-type': 'Person',
    'search-body': builder.build(),
  };

  // Execute the search via the shared request builder. The demo handler still
  // intercepts the POST /api/v2/search builder request (builder-kind matcher),
  // so demo sessions stay inert. Unwrap `.content` for the raw OpenSearch body.
  const { content } = await store.request<OpenSearchResponse<PersonResponse>>(
    executeSearchRequest(query)
  );

  // Parse response using OpenSearchResponseBuilder
  const responseBuilder = OpenSearchResponseBuilder.from<PersonResponse>(content);
  const documents = responseBuilder.getDocuments();

  // Build result map: employeeId -> person data
  const resultMap = new Map<string, EmployeeLookupResult>();

  documents.forEach((person) => {
    // Person documents can have multiple employee IDs (multi-company employees)
    // The employee_ids field from OpenSearch gets normalized to employeeIds (camelCase)
    // Convert numbers to strings for comparison with our stringIds
    const personEmployeeIds = (person.employeeIds || []).map((id) => String(id));

    // Match the requested IDs to this person record (comparing as strings)
    const matchingIds = stringIds.filter((id) => personEmployeeIds.includes(id));

    matchingIds.forEach((empId) => {
      resultMap.set(empId, {
        employeeId: empId,
        fullName: person.fullName || `${person.firstName || ''} ${person.lastName || ''}`.trim(),
        firstName: person.firstName || '',
        lastName: person.lastName || '',
        email: person.email,
      });
    });
  });

  return resultMap;
}
